import type { FC, ReactNode } from 'react';
import { BookText, FileText, LucideFileCheck, Eye, Download } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Loader } from '@/components/ui';
import { usePublicationStats } from '@/lib/api/hooks/publication';

interface StatsCardProps {
	title: string;
	value: number;
	description: ReactNode;
	icon: ReactNode;
	iconContainerClassName: string;
	iconClassName: string;
}

type StatKey = 'total' | 'totalPublished' | 'totalDrafts' | 'totalViews' | 'totalDownloads';
type ChangeKey =
	| 'totalPercentageChange'
	| 'totalPublishedPercentageChange'
	| 'totalDraftsPercentageChange'
	| 'totalViewsPercentageChange'
	| 'totalDownloadsPercentageChange';

type StatsValueMap = Record<StatKey, number> & Record<ChangeKey, number>;

interface StatConfig {
	key: StatKey;
	title: string;
	icon: LucideIcon;
	iconContainerClassName: string;
	iconClassName: string;
	changeKey: ChangeKey;
}

const StatCard = ({
	title,
	value,
	description,
	icon,
	iconContainerClassName,
	iconClassName,
}: StatsCardProps) => {
	return (
		<article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-12 w-12 items-center justify-center rounded-md ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div>
					<p className="text-base font-medium text-gray-600">{title}</p>
					<p className="text-[2rem] leading-[1.1] font-semibold text-gray-900">{value.toLocaleString()}</p>
					<div className="text-sm text-gray-500">{description}</div>
				</div>
			</div>
		</article>
	);
};

const formatChangeValue = (value: number): string => {
	const absValue = Math.abs(value);
	const formatted = Number.isInteger(absValue)
		? absValue.toString()
		: absValue.toFixed(1).replace(/\.0$/, '');

	if (value > 0) return `+${formatted}%`;
	if (value < 0) return `-${formatted}%`;
	return `+${formatted}%`;
};

const getChangeClass = (value: number): string => {
	if (value < 0) return 'text-red-500';
	return 'text-[#0F8A47]';
};

const getDescription = (change: number): ReactNode => {
	return (
		<p>
			<span className={`font-semibold ${getChangeClass(change)}`}>{formatChangeValue(change)}</span>{' '}
			from last 30 days
		</p>
	);
};

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'total',
		title: 'Total Publications',
		icon: BookText,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
		changeKey: 'totalPercentageChange',
	},
	{
		key: 'totalPublished',
		title: 'Published',
		icon: FileText,
		iconContainerClassName: 'bg-[#F8E8EB80]',
		iconClassName: 'text-[#8F2F44]',
		changeKey: 'totalPublishedPercentageChange',
	},
	{
		key: 'totalDrafts',
		title: 'Drafts',
		icon: LucideFileCheck,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#B86A00]',
		changeKey: 'totalDraftsPercentageChange',
	},
	{
		key: 'totalViews',
		title: 'Total Views',
		icon: Eye,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
		changeKey: 'totalViewsPercentageChange',
	},
	{
		key: 'totalDownloads',
		title: 'Total Downloads',
		icon: Download,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
		changeKey: 'totalDownloadsPercentageChange',
	},
];

const PublicationsStats: FC = () => {
	const { data: stats, isFetching, refetch, isError } = usePublicationStats();

	const values: StatsValueMap = {
		total: stats?.total ?? 0,
		totalPublished: stats?.totalPublished ?? 0,
		totalDrafts: stats?.totalDrafts ?? 0,
		totalViews: stats?.totalViews ?? 0,
		totalDownloads: stats?.totalDownloads ?? 0,
		totalPercentageChange: stats?.totalPercentageChange ?? 0,
		totalPublishedPercentageChange: stats?.totalPublishedPercentageChange ?? 0,
		totalDraftsPercentageChange: stats?.totalDraftsPercentageChange ?? 0,
		totalViewsPercentageChange: stats?.totalViewsPercentageChange ?? 0,
		totalDownloadsPercentageChange: stats?.totalDownloadsPercentageChange ?? 0,
	};

	return (
		<section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:flex">
			{isError ? (
				<div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4 sm:col-span-2 xl:col-span-5">
					<p>Error loading statistics.</p>
					<button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
				</div>
			) : isFetching ? (
				STATS_CONFIG.map((stat) => {
					return (
						<div key={stat.key} className="p-4 flex items-center justify-center h-full min-h-28 w-full">
							<Loader size="md" variant="wine" />
						</div>
					);
				})
			) : (
				STATS_CONFIG.map((stat) => {
					const Icon = stat.icon;

					return (
						<StatCard
							key={stat.key}
							title={stat.title}
							value={values[stat.key]}
							description={getDescription(values[stat.changeKey])}
							icon={<Icon className="h-4 w-4 md:h-6 md:w-6" />}
							iconContainerClassName={stat.iconContainerClassName}
							iconClassName={stat.iconClassName}
						/>
					);
				})
			)}
		</section>
	);
};

export default PublicationsStats;
